/** Known Facebook group IDs → display names (English; used as proper nouns). */
export const KNOWN_FACEBOOK_GROUPS: Readonly<Record<string, string>> = {
  '153085616511960': 'Street Dogs of Aruba',
  '172621419426054': 'Lost Pets Aruba',
}

const FACEBOOK_URL_RE =
  /\b(?:https?:\/\/)?(?:www\.|m\.|web\.)?(?:facebook\.com|fb\.com)\/[^\s<>"'\]\)]+/gi

export type TextPart =
  | { type: 'text'; value: string }
  | { type: 'facebook'; href: string; groupId: string | null }

function trimUrlEdgePunctuation(raw: string): string {
  return raw.replace(/[.,;:!?]+$/g, '')
}

export function normalizeFacebookHref(raw: string): string {
  const trimmed = trimUrlEdgePunctuation(raw.trim())
  if (/^https?:\/\//i.test(trimmed)) return trimmed
  return `https://${trimmed}`
}

export function facebookGroupIdFromUrl(href: string): string | null {
  const match = href.match(/\/groups\/(\d+)/i)
  return match?.[1] ?? null
}

export function knownFacebookGroupName(groupId: string | null): string | null {
  if (!groupId) return null
  return KNOWN_FACEBOOK_GROUPS[groupId] ?? null
}

/** Split free text into plain segments and Facebook URL segments. */
export function splitFacebookUrls(text: string): TextPart[] {
  if (!text) return []

  const parts: TextPart[] = []
  let lastIndex = 0
  const re = new RegExp(FACEBOOK_URL_RE.source, FACEBOOK_URL_RE.flags)

  for (const match of text.matchAll(re)) {
    const raw = match[0]
    const index = match.index ?? 0
    const trimmed = trimUrlEdgePunctuation(raw)
    if (!trimmed) continue

    if (index > lastIndex) {
      parts.push({ type: 'text', value: text.slice(lastIndex, index) })
    }

    const href = normalizeFacebookHref(trimmed)
    parts.push({
      type: 'facebook',
      href,
      groupId: facebookGroupIdFromUrl(href),
    })
    // Leave any trailing punctuation for the next text segment.
    lastIndex = index + trimmed.length
  }

  if (lastIndex < text.length) {
    parts.push({ type: 'text', value: text.slice(lastIndex) })
  }

  return parts.length > 0 ? parts : [{ type: 'text', value: text }]
}

export function hasFacebookUrl(text: string): boolean {
  return splitFacebookUrls(text).some((part) => part.type === 'facebook')
}
